<template>
  <div id="app" :class="{ 'app--with-tabbar': showTabbar }">
    <template v-if="isConfigLoaded">
      <router-view v-slot="{ Component }">
        <component :is="Component" />
      </router-view>

      <Teleport to="body">
        <van-tabbar
          v-if="showTabbar"
          route
          fixed
          class="custom-tabbar"
          style="z-index: 99"
        >
          <van-tabbar-item
            v-for="(item, index) in menuList"
            :key="index"
            :to="item.action === 'service' ? undefined : item.path"
            :icon="item.icon"
            @click="onTabClick(item)"
            >{{ item.name }}</van-tabbar-item
          >
        </van-tabbar>
      </Teleport>
    </template>
    <div v-else class="loading-container">
      <van-loading type="spinner" color="#1989fa" size="24px" vertical>
        加载中...
      </van-loading>
    </div>
  </div>
</template>

<script setup>
import { computed, provide, ref } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { Toast } from "vant";
import { openCustomerService } from "@/utils/gate";

const store = useStore();
const route = useRoute();

const overlayOpenCount = ref(0);

const setAppOverlayOpen = (open) => {
  if (open) {
    overlayOpenCount.value += 1;
  } else {
    overlayOpenCount.value = Math.max(0, overlayOpenCount.value - 1);
  }
};

provide("setAppOverlayOpen", setAppOverlayOpen);

// 判断配置是否加载完成
const isConfigLoaded = computed(() => {
  return !!store.state.config.webViewConfig;
});

const SERVICE_TAB = {
  name: "在线客服",
  icon: "service-o",
  action: "service",
};

const menuList = computed(() => {
  const config = store.state.config.webViewConfig;
  const list = Array.isArray(config?.page_menu_list)
    ? [...config.page_menu_list]
    : [];
  const hasService = list.some(
    (item) =>
      item?.action === "service" ||
      item?.name === "在线客服" ||
      item?.path === "/service"
  );
  if (!hasService) {
    list.push({ ...SERVICE_TAB });
  }
  return list;
});

const routeMenuList = computed(() =>
  menuList.value.filter((item) => item?.action !== "service" && item?.path)
);

// 判断当前页面是否应该显示底部菜单
const shouldShowTabbar = computed(() => {
  // 如果路由菜单中包含当前路径，则显示底部菜单
  const currentPath = route.path;

  // 检查当前路径是否在菜单列表中（客服入口不参与路由匹配）
  const inMenuList = routeMenuList.value.some(
    (item) => item.path === currentPath
  );

  // 特殊情况处理：如果是二级页面，可能需要隐藏底部菜单
  // 例如：/address/edit/1 这样的路径不应该显示底部菜单
  const isSubPage = currentPath.split("/").length > 2;

  return inMenuList && !isSubPage;
});

const showTabbar = computed(
  () => shouldShowTabbar.value && overlayOpenCount.value === 0
);

const onTabClick = (item) => {
  if (item?.action !== "service") return;
  const config = store.state.config?.webViewConfig || {};
  if (!openCustomerService(config)) {
    Toast("客服链接未配置");
  }
};
</script>

<style>
#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  max-width: 100%;
}

#app.app--with-tabbar {
  padding-bottom: 50px;
}

.custom-tabbar .van-tabbar-item {
  color: #333; /* 默认字体颜色 */
}

.custom-tabbar .van-tabbar-item--active {
  color: #ff6a00;
}

/* 覆盖 WebView 注入的 bottom 偏移，强制贴底 */
html body .van-tabbar.custom-tabbar {
  position: fixed !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  transform: none !important;
  margin: 0 !important;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}
</style>
